import type { ExerciseType, LocalizedList, LocalizedText, Phase, WorkoutKind } from "./types";

export type ExerciseSeed = {
  slug: string;
  name: LocalizedText;
  type: ExerciseType;
  muscles: string[];
  equipment: string;
  videoUrl: string | null;
  weightStep: number;
  setup: LocalizedText;
  instructions: LocalizedList;
};

export type WorkoutExerciseSeed = {
  slug: string;
  sets: number;
  targetMin: number;
  targetMax: number;
  restSec: number;
  warmupSets?: number;
  alternative: LocalizedText;
};

export type PlanTemplate = {
  key: string;
  name: LocalizedText;
  description: LocalizedText;
  trainingDays: number[];
  cardioDays: number[];
  warmup: LocalizedList;
  tips: LocalizedList;
  phases: Phase[];
  workouts: {
    name: LocalizedText;
    focus: LocalizedText;
    kind: WorkoutKind;
    exercises: WorkoutExerciseSeed[];
  }[];
};

// Language-neutral link; the video component adds the app's language when opening it.
const mw = (slug: string) => `https://musclewiki.com/exercise/${slug}`;
// Exercises without a curated video link to a YouTube search (opens in a new tab).
const yt = (query: string) => `https://www.youtube.com/results?search_query=${encodeURIComponent(`${query} proper form`)}`;

export const EXERCISES: ExerciseSeed[] = [
  {
    slug: "machine-leg-press",
    name: { de: "Beinpresse", en: "Leg press" },
    type: "reps",
    muscles: ["quads", "glutes", "hamstrings"],
    equipment: "machine",
    videoUrl: mw("machine-leg-press"),
    weightStep: 5,
    setup: {
      de: "Rückenlehne so einstellen, dass deine Knie unten etwa 90° gebeugt sind. Füße schulterbreit mittig auf die Platte.",
      en: "Set the backrest so your knees bend to about 90° at the bottom. Feet shoulder-width, centred on the platform.",
    },
    instructions: {
      de: [
        "Rücken und Po fest an die Lehne, Griffe seitlich festhalten.",
        "Sicherung lösen und die Platte 2 Sekunden kontrolliert zu dir ablassen.",
        "Knie zeigen in Richtung Zehen, Po bleibt auf dem Polster.",
        "Über die ganze Fußsohle wegdrücken, Knie oben nicht ganz durchstrecken.",
      ],
      en: [
        "Back and hips pressed into the pad, hold the side handles.",
        "Release the safety and lower the sled towards you for 2 seconds.",
        "Knees track over your toes, hips stay on the seat.",
        "Push through your whole foot; don't fully lock your knees at the top.",
      ],
    },
  },
  {
    slug: "machine-chest-press",
    name: { de: "Brustpresse (Maschine)", en: "Machine chest press" },
    type: "reps",
    muscles: ["chest", "triceps", "shoulders"],
    equipment: "machine",
    videoUrl: mw("machine-chest-press"),
    weightStep: 2.5,
    setup: {
      de: "Sitz so hoch einstellen, dass die Griffe auf Höhe der Brustmitte sind.",
      en: "Adjust the seat so the handles are level with the middle of your chest.",
    },
    instructions: {
      de: [
        "Schulterblätter nach hinten-unten ziehen und so am Polster lassen.",
        "Griffe gleichmäßig nach vorne drücken, ohne die Ellbogen ganz zu strecken.",
        "2 Sekunden kontrolliert zurück, bis du eine leichte Dehnung in der Brust spürst.",
        "Ellbogen bleiben leicht unter Schulterhöhe.",
      ],
      en: [
        "Pull your shoulder blades back and down and keep them on the pad.",
        "Press the handles forward evenly without locking your elbows.",
        "Return for 2 seconds until you feel a light stretch in your chest.",
        "Keep your elbows slightly below shoulder height.",
      ],
    },
  },
  {
    slug: "machine-pulldown",
    name: { de: "Latzug zur Brust", en: "Lat pulldown" },
    type: "reps",
    muscles: ["lats", "upper_back", "biceps"],
    equipment: "cable",
    videoUrl: mw("machine-pulldown"),
    weightStep: 2.5,
    setup: {
      de: "Knie-Polster so einstellen, dass deine Oberschenkel fest darunter klemmen.",
      en: "Set the knee pad so your thighs are locked firmly underneath.",
    },
    instructions: {
      de: [
        "Stange etwas breiter als schulterbreit greifen.",
        "Brust raus, leicht nach hinten lehnen.",
        "Stange zur oberen Brust ziehen, Ellbogen nach unten Richtung Hüfte.",
        "2 Sekunden kontrolliert nach oben lassen, bis die Arme gestreckt sind.",
      ],
      en: [
        "Grip the bar slightly wider than shoulder width.",
        "Chest up, lean back slightly.",
        "Pull the bar to your upper chest, driving your elbows down to your hips.",
        "Let it rise for 2 seconds until your arms are straight.",
      ],
    },
  },
  {
    slug: "machine-seated-leg-curl",
    name: { de: "Beinbeuger sitzend", en: "Seated leg curl" },
    type: "reps",
    muscles: ["hamstrings"],
    equipment: "machine",
    videoUrl: mw("machine-seated-leg-curl"),
    weightStep: 2.5,
    setup: {
      de: "Kniegelenk auf Höhe des Drehpunkts (meist ein roter Punkt). Fersenpolster knapp über den Fersen, Oberschenkelpolster fest.",
      en: "Line up your knee with the machine's pivot (often a red dot). Ankle pad just above your heels, thigh pad snug.",
    },
    instructions: {
      de: [
        "Griffe festhalten, Rücken an die Lehne.",
        "Fersen nach unten-hinten ziehen, so weit es geht.",
        "Kurz halten, dann 2 Sekunden kontrolliert zurück.",
      ],
      en: [
        "Hold the handles, back against the pad.",
        "Pull your heels down and back as far as you can.",
        "Pause briefly, then return for 2 seconds.",
      ],
    },
  },
  {
    slug: "machine-neutral-overhead-press",
    name: { de: "Schulterpresse (Maschine)", en: "Machine shoulder press" },
    type: "reps",
    muscles: ["shoulders", "triceps"],
    equipment: "machine",
    videoUrl: mw("machine-neutral-overhead-press"),
    weightStep: 2.5,
    setup: {
      de: "Sitz so einstellen, dass die Griffe etwa auf Schulterhöhe sind.",
      en: "Set the seat so the handles start at about shoulder height.",
    },
    instructions: {
      de: [
        "Rücken fest an die Lehne, Bauch leicht anspannen.",
        "Griffe nach oben drücken, ohne die Ellbogen durchzustrecken.",
        "2 Sekunden kontrolliert zurück bis auf Schulterhöhe.",
        "Kein Hohlkreuz: Rippen unten lassen.",
      ],
      en: [
        "Back firmly against the pad, brace your abs lightly.",
        "Press the handles up without locking your elbows.",
        "Lower for 2 seconds back to shoulder height.",
        "Don't arch your lower back: keep your ribs down.",
      ],
    },
  },
  {
    slug: "cable-rope-pushdown",
    name: { de: "Trizepsdrücken am Kabel (Seil)", en: "Cable rope pushdown" },
    type: "reps",
    muscles: ["triceps"],
    equipment: "cable",
    videoUrl: mw("cable-rope-pushdown"),
    weightStep: 2.5,
    setup: {
      de: "Seil-Griff oben am Kabelzug einhängen. Rolle ganz nach oben stellen.",
      en: "Attach the rope to the cable and set the pulley to the highest position.",
    },
    instructions: {
      de: [
        "Aufrecht stehen, Ellbogen eng am Körper.",
        "Seil nach unten drücken, unten die Enden leicht auseinanderziehen.",
        "Nur die Unterarme bewegen sich, die Ellbogen bleiben stehen.",
        "2 Sekunden kontrolliert zurück bis knapp über 90°.",
      ],
      en: [
        "Stand tall, elbows tucked to your sides.",
        "Push the rope down and spread the ends slightly at the bottom.",
        "Only your forearms move; your elbows stay put.",
        "Return for 2 seconds to just above 90°.",
      ],
    },
  },
  {
    slug: "forearm-plank",
    name: { de: "Unterarmstütz (Plank)", en: "Forearm plank" },
    type: "time",
    muscles: ["abs"],
    equipment: "bodyweight",
    videoUrl: mw("forearm-plank"),
    weightStep: 0,
    setup: {
      de: "Auf eine Matte legen. Ellbogen unter die Schultern.",
      en: "Lie on a mat with your elbows under your shoulders.",
    },
    instructions: {
      de: [
        "Auf Unterarme und Zehenspitzen stützen.",
        "Körper bildet eine gerade Linie vom Kopf bis zu den Fersen.",
        "Po und Bauch anspannen, Hüfte nicht durchhängen lassen.",
        "Ruhig weiteratmen und die Zeit halten.",
      ],
      en: [
        "Support yourself on your forearms and toes.",
        "Make a straight line from head to heels.",
        "Squeeze glutes and abs; don't let your hips sag.",
        "Keep breathing calmly and hold for the time.",
      ],
    },
  },
  {
    slug: "smith-machine-squat",
    name: {
      de: "Kniebeuge an der Multipresse",
      en: "Smith machine squat",
    },
    type: "reps",
    muscles: ["quads", "glutes", "hamstrings"],
    equipment: "smith",
    videoUrl: mw("smith-machine-squat"),
    weightStep: 2.5,
    setup: {
      de: "Stange etwa auf Schulterhöhe einhängen. Sicherungsanschläge knapp unter deine tiefste Position stellen.",
      en: "Rack the bar at about shoulder height. Set the safety stops just below your lowest position.",
    },
    instructions: {
      de: [
        "Stange auf den oberen Rücken (nicht auf den Nacken) legen, Füße leicht vor dem Körper.",
        "Stange durch Drehen aushängen.",
        "Hüfte nach hinten und unten, als würdest du dich setzen, Knie Richtung Zehen.",
        "So tief, wie du mit geradem Rücken kommst, dann kräftig hochdrücken.",
        "Zum Ende die Stange wieder durch Drehen einhängen.",
      ],
      en: [
        "Bar on your upper back (not your neck), feet slightly in front of you.",
        "Rotate the bar to unhook it.",
        "Sit your hips back and down, knees tracking over your toes.",
        "Go as deep as you can with a straight back, then drive up.",
        "At the end, rotate the bar to hook it back in.",
      ],
    },
  },
  {
    slug: "machine-seated-cable-row",
    name: { de: "Rudern sitzend (Maschine)", en: "Seated row (machine)" },
    type: "reps",
    muscles: ["upper_back", "lats", "biceps"],
    equipment: "machine",
    videoUrl: mw("machine-seated-cable-row"),
    weightStep: 2.5,
    setup: {
      de: "Brustpolster so einstellen, dass du die Griffe mit gestreckten Armen gerade noch erreichst.",
      en: "Set the chest pad so you can just reach the handles with straight arms.",
    },
    instructions: {
      de: [
        "Brust ans Polster, aufrecht sitzen.",
        "Griffe zum Bauch ziehen, Schulterblätter zusammenführen.",
        "Kurz halten, Schultern bleiben unten.",
        "2 Sekunden kontrolliert nach vorne lassen.",
      ],
      en: [
        "Chest on the pad, sit tall.",
        "Pull the handles towards your stomach, squeezing your shoulder blades together.",
        "Pause briefly; keep your shoulders down.",
        "Let them go forward for 2 seconds.",
      ],
    },
  },
  {
    slug: "machine-plate-loaded-incline-chest-press",
    name: {
      de: "Schrägbankdrücken (Maschine)",
      en: "Incline chest press (machine)",
    },
    type: "reps",
    muscles: ["chest", "shoulders", "triceps"],
    equipment: "machine",
    videoUrl: mw("machine-plate-loaded-incline-chest-press"),
    weightStep: 2.5,
    setup: {
      de: "Sitz so einstellen, dass die Griffe auf Höhe der oberen Brust sind.",
      en: "Adjust the seat so the handles are level with your upper chest.",
    },
    instructions: {
      de: [
        "Schulterblätter nach hinten-unten, Rücken an die Lehne.",
        "Griffe schräg nach oben-vorne drücken.",
        "Ellbogen oben nicht ganz durchstrecken.",
        "2 Sekunden kontrolliert zurück.",
      ],
      en: [
        "Shoulder blades back and down, back on the pad.",
        "Press the handles up and forward at an angle.",
        "Don't fully lock your elbows at the top.",
        "Return for 2 seconds.",
      ],
    },
  },
  {
    slug: "machine-45-degree-back-extension",
    name: {
      de: "Rückenstrecker (Hyperextension)",
      en: "45° back extension",
    },
    type: "reps",
    muscles: ["lower_back", "glutes", "hamstrings"],
    equipment: "bodyweight",
    videoUrl: mw("machine-45-degree-back-extension"),
    weightStep: 2.5,
    setup: {
      de: "Polster so einstellen, dass die Oberkante knapp unter deinem Hüftknochen liegt.",
      en: "Set the pad so its top edge sits just below your hip bones.",
    },
    instructions: {
      de: [
        "Füße fest unter die Rolle, Arme vor der Brust kreuzen.",
        "Mit geradem Rücken aus der Hüfte nach vorne-unten beugen.",
        "Po anspannen und hochkommen, bis der Körper eine Linie bildet.",
        "Oben nicht ins Hohlkreuz überstrecken.",
      ],
      en: [
        "Feet locked under the roller, arms crossed over your chest.",
        "Hinge forward and down at the hips with a straight back.",
        "Squeeze your glutes and rise until your body forms a straight line.",
        "Don't hyperextend your lower back at the top.",
      ],
    },
  },
  {
    slug: "machine-standing-lateral-raise",
    name: { de: "Seitheben (Maschine)", en: "Machine lateral raise" },
    type: "reps",
    muscles: ["shoulders"],
    equipment: "machine",
    videoUrl: mw("machine-standing-lateral-raise"),
    weightStep: 2.5,
    setup: {
      de: "Sitz bzw. Polster so einstellen, dass deine Schultern auf Höhe des Drehpunkts sind.",
      en: "Adjust the seat or pads so your shoulders line up with the pivot.",
    },
    instructions: {
      de: [
        "Arme bzw. Ellbogen seitlich an die Polster legen.",
        "Arme seitlich bis auf Schulterhöhe heben, nicht höher.",
        "Schultern bleiben unten, nicht hochziehen.",
        "2 Sekunden kontrolliert absenken.",
      ],
      en: [
        "Place your arms or elbows against the side pads.",
        "Raise your arms out to the sides up to shoulder height, no higher.",
        "Keep your shoulders down; don't shrug.",
        "Lower for 2 seconds.",
      ],
    },
  },
  {
    slug: "cable-bar-curl",
    name: { de: "Bizepscurl am Kabel", en: "Cable bar curl" },
    type: "reps",
    muscles: ["biceps"],
    equipment: "cable",
    videoUrl: mw("cable-bar-curl"),
    weightStep: 2.5,
    setup: {
      de: "Gerade Stange unten am Kabelzug einhängen, Rolle ganz nach unten.",
      en: "Attach a straight bar to the cable and set the pulley at the lowest position.",
    },
    instructions: {
      de: [
        "Stange schulterbreit im Untergriff (Handflächen nach oben) greifen.",
        "Ellbogen bleiben eng am Körper.",
        "Stange Richtung Schultern beugen, ohne Schwung aus dem Rücken.",
        "2 Sekunden kontrolliert absenken, bis die Arme fast gestreckt sind.",
      ],
      en: [
        "Grip the bar shoulder-width with palms facing up.",
        "Keep your elbows tucked to your sides.",
        "Curl the bar towards your shoulders without swinging.",
        "Lower for 2 seconds until your arms are almost straight.",
      ],
    },
  },
  {
    slug: "machine-crunch",
    name: { de: "Bauchmaschine (Crunch)", en: "Machine crunch" },
    type: "reps",
    muscles: ["abs"],
    equipment: "machine",
    videoUrl: mw("machine-crunch"),
    weightStep: 2.5,
    setup: {
      de: "Sitz so einstellen, dass die Griffe bzw. Polster auf Höhe deiner Schultern sind.",
      en: "Adjust the seat so the handles or pads sit at shoulder height.",
    },
    instructions: {
      de: [
        "Griffe festhalten, Füße unter die Rollen.",
        "Oberkörper einrollen: Rippen Richtung Becken ziehen.",
        "Die Bewegung kommt aus dem Bauch, nicht aus den Armen.",
        "2 Sekunden kontrolliert zurück, ohne die Gewichte abzulegen.",
      ],
      en: [
        "Hold the handles, feet under the rollers.",
        "Curl your torso: pull your ribs towards your pelvis.",
        "The movement comes from your abs, not your arms.",
        "Return for 2 seconds without letting the weights touch down.",
      ],
    },
  },
  {
    slug: "treadmill-run",
    name: { de: "Laufband (Gesprächstempo)", en: "Treadmill (easy pace)" },
    type: "cardio",
    muscles: ["cardio"],
    equipment: "cardio",
    videoUrl: null,
    weightStep: 0,
    setup: {
      de: "Steigung auf 1 %. Starte bei 8,5–9,5 km/h. 10 km/h entsprechen etwa 6 min/km.",
      en: "Set the incline to 1%. Start at 8.5–9.5 km/h. 10 km/h is about 6 min/km.",
    },
    instructions: {
      de: [
        "So locker laufen, dass du dich noch unterhalten könntest.",
        "Ab Woche 5: Dauer um höchstens 10 % pro Woche steigern.",
        "Oder: 4–6 × 1 Min. schneller, dazwischen 1 Min. locker.",
      ],
      en: [
        "Run easy enough that you could still hold a conversation.",
        "From week 5: increase the duration by at most 10% per week.",
        "Or: 4–6 × 1 min faster with 1 min easy in between.",
      ],
    },
  },
  // --- added with the upper/lower, push/pull/legs and home templates -------------------
  {
    slug: "machine-leg-extension",
    name: { de: "Beinstrecker", en: "Leg extension" },
    type: "reps",
    muscles: ["quads"],
    equipment: "machine",
    videoUrl: yt("machine leg extension"),
    weightStep: 2.5,
    setup: {
      de: "Rückenlehne so einstellen, dass deine Kniegelenke auf Höhe des Drehpunkts liegen. Das Fußpolster liegt knapp über den Knöcheln.",
      en: "Set the backrest so your knees line up with the machine's pivot. The foot pad sits just above your ankles.",
    },
    instructions: {
      de: [
        "Griffe seitlich festhalten, der Po bleibt auf dem Sitz.",
        "Beine kontrolliert strecken, bis die Knie fast gerade sind.",
        "Oben kurz halten und die Oberschenkel anspannen.",
        "2 Sekunden kontrolliert absenken, ohne das Gewicht abzulegen.",
      ],
      en: [
        "Hold the side handles and keep your hips on the seat.",
        "Straighten your legs under control until your knees are almost straight.",
        "Pause briefly at the top and squeeze your thighs.",
        "Lower for 2 seconds without letting the weight rest.",
      ],
    },
  },
  {
    slug: "machine-pec-fly",
    name: { de: "Butterfly an der Maschine", en: "Pec deck (machine fly)" },
    type: "reps",
    muscles: ["chest", "shoulders"],
    equipment: "machine",
    videoUrl: yt("pec deck machine fly"),
    weightStep: 2.5,
    setup: {
      de: "Sitz so einstellen, dass die Griffe auf Brusthöhe sind. Die Arme bleiben leicht gebeugt.",
      en: "Adjust the seat so the handles are at chest height. Keep a slight bend in your arms.",
    },
    instructions: {
      de: [
        "Rücken ans Polster, Schulterblätter nach hinten-unten ziehen.",
        "Die Arme in einem weiten Bogen vor der Brust zusammenführen.",
        "Vorne kurz die Brust anspannen.",
        "2 Sekunden zurück, bis du eine leichte Dehnung spürst, nicht weiter.",
      ],
      en: [
        "Back against the pad, shoulder blades back and down.",
        "Bring your arms together in front of your chest in a wide arc.",
        "Squeeze your chest briefly at the front.",
        "Return for 2 seconds until you feel a light stretch, no further.",
      ],
    },
  },
  {
    slug: "cable-face-pull",
    name: { de: "Face Pulls am Kabel", en: "Cable face pull" },
    type: "reps",
    muscles: ["upper_back", "shoulders"],
    equipment: "cable",
    videoUrl: yt("cable face pull"),
    weightStep: 2.5,
    setup: {
      de: "Seilgriff am Kabelzug etwa auf Augenhöhe einhängen.",
      en: "Attach the rope to the cable at about eye level.",
    },
    instructions: {
      de: [
        "Seil mit beiden Händen greifen, die Daumen zeigen nach hinten.",
        "Einen Schritt zurückgehen, bis das Gewicht abhebt, und stabil stehen.",
        "Seil zum Gesicht ziehen, die Hände dabei auseinander, Ellbogen hoch.",
        "Kurz halten und 2 Sekunden kontrolliert zurück.",
      ],
      en: [
        "Grab the rope with both hands, thumbs pointing back.",
        "Step back until the weight lifts and stand firmly.",
        "Pull the rope towards your face, hands apart, elbows high.",
        "Hold briefly and return for 2 seconds.",
      ],
    },
  },
  {
    slug: "machine-calf-raise",
    name: { de: "Wadenheben an der Maschine", en: "Machine calf raise" },
    type: "reps",
    muscles: ["calves"],
    equipment: "machine",
    videoUrl: yt("standing calf raise machine"),
    weightStep: 5,
    setup: {
      de: "Schulterpolster so einstellen, dass du aufrecht darunter stehst. Fußballen auf die Kante, die Fersen sind frei.",
      en: "Set the shoulder pads so you stand upright beneath them. Balls of your feet on the edge, heels free.",
    },
    instructions: {
      de: [
        "Knie gerade, aber nicht durchgedrückt.",
        "Fersen langsam so tief wie möglich absenken.",
        "Unten 1 Sekunde Pause, dann so hoch wie möglich auf die Zehenspitzen drücken.",
        "Oben kurz halten, ohne zu wippen.",
      ],
      en: [
        "Knees straight but not locked.",
        "Lower your heels slowly as far as you can.",
        "Pause for a second at the bottom, then rise onto your toes as high as you can.",
        "Hold briefly at the top without bouncing.",
      ],
    },
  },
  {
    slug: "cable-overhead-triceps-extension",
    name: { de: "Trizepsstrecken über Kopf am Kabel", en: "Cable overhead triceps extension" },
    type: "reps",
    muscles: ["triceps"],
    equipment: "cable",
    videoUrl: yt("cable overhead triceps extension"),
    weightStep: 2.5,
    setup: {
      de: "Seilgriff unten am Kabelzug einhängen. Das Seil hinter den Kopf nehmen und mit dem Rücken zum Gerät einen Schritt nach vorne gehen, ein Fuß vor dem anderen.",
      en: "Attach the rope low on the cable. Take it behind your head and step forward with your back to the machine, one foot in front of the other.",
    },
    instructions: {
      de: [
        "Leicht nach vorne lehnen, die Ellbogen zeigen nach vorne-oben und bleiben an Ort und Stelle.",
        "Unterarme strecken, bis die Arme fast gerade sind.",
        "Oben kurz den Trizeps anspannen.",
        "2 Sekunden kontrolliert zurück, bis du eine Dehnung im Oberarm spürst.",
      ],
      en: [
        "Lean forward slightly; your elbows point forward and up and stay in place.",
        "Extend your forearms until your arms are almost straight.",
        "Squeeze your triceps briefly.",
        "Return for 2 seconds until you feel a stretch in your upper arm.",
      ],
    },
  },
  {
    slug: "dumbbell-romanian-deadlift",
    name: { de: "Rumänisches Kreuzheben mit Kurzhanteln", en: "Dumbbell Romanian deadlift" },
    type: "reps",
    muscles: ["hamstrings", "glutes", "lower_back"],
    equipment: "dumbbell",
    videoUrl: yt("dumbbell romanian deadlift"),
    weightStep: 2,
    setup: {
      de: "Hüftbreit stehen, in jeder Hand eine Kurzhantel vor den Oberschenkeln.",
      en: "Stand hip-width apart with a dumbbell in each hand in front of your thighs.",
    },
    instructions: {
      de: [
        "Knie leicht beugen und so lassen.",
        "Hüfte nach hinten schieben, Rücken gerade, die Hanteln gleiten nah an den Beinen nach unten.",
        "Absenken, bis du eine deutliche Dehnung hinten im Oberschenkel spürst (meist knapp unter dem Knie).",
        "Po anspannen und die Hüfte nach vorne bringen, bis du aufrecht stehst.",
      ],
      en: [
        "Bend your knees slightly and keep them there.",
        "Push your hips back with a flat back; the dumbbells slide down close to your legs.",
        "Lower until you feel a clear stretch in your hamstrings (usually just below the knee).",
        "Squeeze your glutes and drive your hips forward until you stand tall.",
      ],
    },
  },
  {
    slug: "dumbbell-hammer-curl",
    name: { de: "Hammercurls mit Kurzhanteln", en: "Dumbbell hammer curl" },
    type: "reps",
    muscles: ["biceps"],
    equipment: "dumbbell",
    videoUrl: yt("dumbbell hammer curl"),
    weightStep: 2,
    setup: {
      de: "Aufrecht stehen, die Kurzhanteln seitlich, Handflächen zeigen zueinander.",
      en: "Stand tall with the dumbbells at your sides, palms facing each other.",
    },
    instructions: {
      de: [
        "Die Ellbogen bleiben seitlich am Körper.",
        "Hanteln ohne Schwung hochbeugen, die Daumen zeigen nach oben.",
        "Oben kurz halten.",
        "2 Sekunden kontrolliert absenken, bis die Arme gestreckt sind.",
      ],
      en: [
        "Keep your elbows at your sides.",
        "Curl the dumbbells up without swinging, thumbs pointing up.",
        "Pause briefly at the top.",
        "Lower for 2 seconds until your arms are straight.",
      ],
    },
  },
  {
    slug: "dumbbell-split-squat",
    name: { de: "Split Squat mit Kurzhanteln", en: "Dumbbell split squat" },
    type: "reps",
    muscles: ["quads", "glutes"],
    equipment: "dumbbell",
    videoUrl: yt("dumbbell split squat"),
    weightStep: 2,
    setup: {
      de: "Großer Schrittstand, in jeder Hand eine Kurzhantel. Die Wiederholungen gelten pro Bein.",
      en: "Take a long split stance with a dumbbell in each hand. Reps are per leg.",
    },
    instructions: {
      de: [
        "Oberkörper aufrecht, das Gewicht liegt auf dem vorderen Fuß.",
        "Das hintere Knie gerade nach unten Richtung Boden senken.",
        "Das vordere Knie zeigt in Richtung Zehen.",
        "Über die vordere Ferse hochdrücken. Erst alle Wiederholungen mit einem Bein, dann wechseln.",
      ],
      en: [
        "Upper body upright, weight on your front foot.",
        "Lower your back knee straight down towards the floor.",
        "Your front knee tracks over your toes.",
        "Push up through your front heel. Do all reps on one leg, then switch.",
      ],
    },
  },
  {
    slug: "dumbbell-goblet-squat",
    name: { de: "Goblet Squat mit Kurzhantel", en: "Dumbbell goblet squat" },
    type: "reps",
    muscles: ["quads", "glutes"],
    equipment: "dumbbell",
    videoUrl: yt("dumbbell goblet squat"),
    weightStep: 2,
    setup: {
      de: "Schulterbreit stehen, Fußspitzen leicht nach außen. Eine Kurzhantel senkrecht vor der Brust halten.",
      en: "Stand shoulder-width, toes turned out slightly. Hold one dumbbell upright in front of your chest.",
    },
    instructions: {
      de: [
        "Brust raus, die Ellbogen zeigen nach unten.",
        "Hüfte nach hinten-unten setzen, die Knie zeigen in Richtung Zehen.",
        "So tief gehen, wie du mit geradem Rücken kommst. Die Fersen bleiben am Boden.",
        "Über die ganze Fußsohle hochdrücken.",
      ],
      en: [
        "Chest up, elbows pointing down.",
        "Sit your hips back and down, knees tracking over your toes.",
        "Go as deep as you can with a flat back, heels on the floor.",
        "Push up through your whole foot.",
      ],
    },
  },
  {
    slug: "dumbbell-floor-press",
    name: { de: "Bodendrücken mit Kurzhanteln", en: "Dumbbell floor press" },
    type: "reps",
    muscles: ["chest", "triceps", "shoulders"],
    equipment: "dumbbell",
    videoUrl: yt("dumbbell floor press"),
    weightStep: 2,
    setup: {
      de: "Rückenlage auf einer Matte, Knie angewinkelt, in jeder Hand eine Kurzhantel. Ersetzt das Bankdrücken, wenn du keine Bank hast.",
      en: "Lie on your back on a mat with your knees bent and a dumbbell in each hand. Replaces the bench press when you have no bench.",
    },
    instructions: {
      de: [
        "Die Oberarme liegen in etwa 45° zum Körper auf dem Boden.",
        "Hanteln über der Brust nach oben drücken, bis die Arme fast gestreckt sind.",
        "2 Sekunden kontrolliert absenken, bis die Oberarme den Boden berühren.",
        "Kurz ablegen und ohne Schwung wieder hochdrücken.",
      ],
      en: [
        "Your upper arms rest on the floor at about 45° to your body.",
        "Press the dumbbells up over your chest until your arms are almost straight.",
        "Lower for 2 seconds until your upper arms touch the floor.",
        "Pause there briefly, then press again without bouncing.",
      ],
    },
  },
  {
    slug: "dumbbell-one-arm-row",
    name: { de: "Einarmiges Kurzhantelrudern", en: "One-arm dumbbell row" },
    type: "reps",
    muscles: ["upper_back", "lats", "biceps"],
    equipment: "dumbbell",
    videoUrl: yt("one arm dumbbell row"),
    weightStep: 2,
    setup: {
      de: "Eine Hand und ein Knie auf eine Bank, einen Stuhl oder das Sofa stützen. Die Wiederholungen gelten pro Seite.",
      en: "Support one hand and one knee on a bench, chair or sofa. Reps are per side.",
    },
    instructions: {
      de: [
        "Rücken gerade und parallel zum Boden.",
        "Die Hantel mit gestrecktem Arm hängen lassen.",
        "Hantel Richtung Hüfte ziehen, der Ellbogen bleibt nah am Körper.",
        "2 Sekunden kontrolliert absenken. Erst alle Wiederholungen auf einer Seite, dann wechseln.",
      ],
      en: [
        "Back flat and parallel to the floor.",
        "Let the dumbbell hang with your arm straight.",
        "Row it towards your hip, elbow close to your body.",
        "Lower for 2 seconds. Do all reps on one side, then switch.",
      ],
    },
  },
  {
    slug: "dumbbell-shoulder-press",
    name: { de: "Kurzhantel-Schulterdrücken", en: "Dumbbell shoulder press" },
    type: "reps",
    muscles: ["shoulders", "triceps"],
    equipment: "dumbbell",
    videoUrl: yt("dumbbell shoulder press"),
    weightStep: 2,
    setup: {
      de: "Aufrecht sitzen oder stehen, die Hanteln auf Schulterhöhe, Handflächen zeigen nach vorne oder zueinander.",
      en: "Sit or stand tall with the dumbbells at shoulder height, palms facing forward or towards each other.",
    },
    instructions: {
      de: [
        "Bauch und Po anspannen, kein Hohlkreuz.",
        "Hanteln nach oben drücken, bis die Arme fast gestreckt sind.",
        "Oben nicht zusammenschlagen.",
        "2 Sekunden kontrolliert zurück auf Schulterhöhe.",
      ],
      en: [
        "Brace your abs and glutes; don't arch your lower back.",
        "Press the dumbbells up until your arms are almost straight.",
        "Don't clank them together at the top.",
        "Lower for 2 seconds back to shoulder height.",
      ],
    },
  },
  {
    slug: "push-up",
    name: { de: "Liegestütz", en: "Push-up" },
    type: "reps",
    muscles: ["chest", "triceps", "shoulders"],
    equipment: "bodyweight",
    videoUrl: yt("push up"),
    weightStep: 0,
    setup: {
      de: "Hände etwas breiter als schulterbreit auf den Boden. Zu schwer? Hände auf eine Bank, einen Tisch oder die Wand stützen: je höher, desto leichter.",
      en: "Hands slightly wider than shoulder width on the floor. Too hard? Put your hands on a bench, table or wall: the higher, the easier.",
    },
    instructions: {
      de: [
        "Der Körper bildet eine gerade Linie vom Kopf bis zu den Fersen.",
        "Ellbogen in etwa 45° zum Körper beugen und die Brust Richtung Boden senken.",
        "Unten kurz halten, der Bauch bleibt angespannt.",
        "Kräftig hochdrücken, bis die Arme gestreckt sind.",
      ],
      en: [
        "Make a straight line from head to heels.",
        "Bend your elbows at about 45° to your body and lower your chest towards the floor.",
        "Pause briefly at the bottom, keeping your abs tight.",
        "Push up strongly until your arms are straight.",
      ],
    },
  },
  {
    slug: "glute-bridge",
    name: { de: "Hüftheben (Glute Bridge)", en: "Glute bridge" },
    type: "reps",
    muscles: ["glutes", "hamstrings"],
    equipment: "bodyweight",
    videoUrl: yt("glute bridge"),
    weightStep: 2,
    setup: {
      de: "Rückenlage, Knie angewinkelt, die Füße hüftbreit nah am Po. Für mehr Gewicht eine Kurzhantel auf die Hüfte legen.",
      en: "Lie on your back, knees bent, feet hip-width and close to your glutes. For more load, rest a dumbbell on your hips.",
    },
    instructions: {
      de: [
        "Die Arme liegen seitlich am Boden.",
        "Über die Fersen die Hüfte hochdrücken, bis Knie, Hüfte und Schultern eine Linie bilden.",
        "Oben 1 Sekunde den Po fest anspannen.",
        "Die Hüfte 2 Sekunden kontrolliert absenken.",
      ],
      en: [
        "Arms on the floor at your sides.",
        "Drive through your heels and lift your hips until knees, hips and shoulders form a line.",
        "Squeeze your glutes hard for a second at the top.",
        "Lower your hips for 2 seconds.",
      ],
    },
  },
  {
    slug: "walk-or-run",
    name: { de: "Zügig gehen oder locker laufen", en: "Brisk walk or easy run" },
    type: "cardio",
    muscles: ["cardio"],
    equipment: "cardio",
    videoUrl: null,
    weightStep: 0,
    setup: {
      de: "Draußen oder auf dem Laufband. Bequeme Schuhe und eine Runde von 2–5 km in der Nähe.",
      en: "Outdoors or on a treadmill. Comfortable shoes and a 2–5 km loop near home.",
    },
    instructions: {
      de: [
        "So zügig, dass du schneller atmest, dich aber noch unterhalten könntest.",
        "Anfangs Gehen und Laufen abwechseln, z. B. 2 Min. gehen, 1 Min. laufen.",
        "Die Dauer um höchstens 10 % pro Woche steigern.",
      ],
      en: [
        "Fast enough that you breathe harder but could still hold a conversation.",
        "At first, alternate walking and running, e.g. 2 min walking, 1 min running.",
        "Increase the duration by at most 10% per week.",
      ],
    },
  },
];

const restHeavy = 90;
const restLight = 60;

export const FULLBODY_STARTER: PlanTemplate = {
  key: "fullbody-starter",
  name: { de: "Ganzkörper-Starter (3×/Woche)", en: "Full-body starter (3×/week)" },
  description: {
    de: "3× pro Woche Ganzkörper, abwechselnd Workout A und B, je ca. 60 Minuten. Fast nur Maschinen und Kabelzüge, die sich ohne Trainer am sichersten lernen lassen. Zwischen zwei Krafttagen immer ein Ruhetag.",
    en: "Full body 3× per week, alternating workout A and B, about 60 minutes each. Almost only machines and cables, which are the safest to learn without a coach. Always one rest day between two strength days.",
  },
  trainingDays: [0, 2, 4],
  cardioDays: [1, 5],
  warmup: {
    de: [
      "5–8 Min. Crosstrainer, Rad oder zügiges Gehen am Laufband, so locker, dass du noch reden kannst.",
      "Je 10× Armkreisen, Hüftkreisen und Kniebeugen ohne Gewicht.",
      "Bei der ersten Übung 1–2 Aufwärmsätze mit ca. 50 % des Arbeitsgewichts.",
    ],
    en: [
      "5–8 min cross-trainer, bike or brisk treadmill walk, easy enough to still talk.",
      "10× each: arm circles, hip circles and bodyweight squats.",
      "For the first exercise, 1–2 warm-up sets at about 50% of your working weight.",
    ],
  },
  tips: {
    de: [
      "Sitz einstellen: Knie-, Schulter- oder Ellbogengelenk auf Höhe des Drehpunkts der Maschine. Viele Geräte haben eine Anleitung als Aufkleber.",
      "Tempo: 2 Sekunden kontrolliert ablassen, ohne Schwung hochdrücken.",
      "Stechender Schmerz im Gelenk heißt abbrechen. Muskelbrennen und Muskelkater sind normal.",
      "Nach einer Pause (Krankheit, Prüfungen) mit ca. 10 % weniger Gewicht wieder einsteigen. Die App schlägt das automatisch vor.",
      "Früh morgens oder spät abends sind die Geräte in den meisten Studios frei.",
      "Optional nach dem Krafttraining: 10 Min. Rad oder Crosstrainer locker zum Auslaufen.",
      "Unsicher an einem Gerät? Frag das Personal deines Studios nach einer Einweisung, das bieten fast alle an.",
    ],
    en: [
      "Seat setup: line up your knee, shoulder or elbow with the machine's pivot. Many machines have an instruction sticker.",
      "Tempo: lower for 2 seconds under control, push up without swinging.",
      "Sharp joint pain means stop. Muscle burn and soreness are normal.",
      "After a break (illness, exams) restart with about 10% less weight. The app suggests this automatically.",
      "Early mornings and late evenings are usually quiet in most gyms.",
      "Optional after strength training: 10 min easy bike or cross-trainer to cool down.",
      "Unsure about a machine? Ask your gym's staff for an introduction, almost every gym offers one.",
    ],
  },
  phases: [
    {
      id: "p1",
      fromWeek: 1,
      toWeek: 2,
      setsMode: "fixed",
      fixedSets: 2,
      rir: "4",
      goal: {
        de: "Geräte einstellen, Technik lernen, Startgewichte notieren. Leicht trainieren.",
        en: "Set up the machines, learn the technique, note your starting weights. Keep it light.",
      },
    },
    {
      id: "p2",
      fromWeek: 3,
      toWeek: 8,
      setsMode: "plan",
      rir: "2–3",
      goal: {
        de: "Gewicht steigern, sobald du in allen Sätzen das obere Ende der Wiederholungen sauber schaffst.",
        en: "Add weight once you hit the top of the rep range cleanly in every set.",
      },
    },
    {
      id: "p3",
      fromWeek: 9,
      toWeek: null,
      setsMode: "bonus",
      bonusSets: 1,
      bonusFirstN: 3,
      rir: "1–2",
      goal: {
        de: "Übung 1–3 je +1 Satz, weiter steigern. Nach Woche 12 ist ein Wechsel auf 4 Tage Ober-/Unterkörper möglich.",
        en: "Exercises 1–3 get +1 set; keep progressing. After week 12 you can switch to a 4-day upper/lower split.",
      },
    },
  ],
  workouts: [
    {
      name: { de: "Workout A", en: "Workout A" },
      focus: {
        de: "Beine (Kniebeuge-Muster), Brust und Latissimus",
        en: "Legs (squat pattern), chest and lats",
      },
      kind: "strength",
      exercises: [
        {
          slug: "machine-leg-press",
          sets: 3,
          targetMin: 10,
          targetMax: 12,
          restSec: restHeavy,
          warmupSets: 2,
          alternative: {
            de: "Hackenschmidt-Maschine oder Goblet Squat mit Kurzhantel",
            en: "Hack squat machine or dumbbell goblet squat",
          },
        },
        {
          slug: "machine-chest-press",
          sets: 3,
          targetMin: 10,
          targetMax: 12,
          restSec: restHeavy,
          alternative: {
            de: "Kurzhantel-Bankdrücken",
            en: "Dumbbell bench press",
          },
        },
        {
          slug: "machine-pulldown",
          sets: 3,
          targetMin: 10,
          targetMax: 12,
          restSec: restHeavy,
          alternative: {
            de: "Lat-Maschine mit Griffen oder Klimmzug-Maschine mit Unterstützung",
            en: "Lat machine with handles or assisted pull-up machine",
          },
        },
        {
          slug: "machine-seated-leg-curl",
          sets: 2,
          targetMin: 12,
          targetMax: 15,
          restSec: restLight,
          alternative: { de: "Beinbeuger liegend", en: "Lying leg curl" },
        },
        {
          slug: "machine-neutral-overhead-press",
          sets: 2,
          targetMin: 10,
          targetMax: 12,
          restSec: restLight,
          alternative: {
            de: "Kurzhantel-Schulterdrücken sitzend",
            en: "Seated dumbbell shoulder press",
          },
        },
        {
          slug: "cable-rope-pushdown",
          sets: 2,
          targetMin: 12,
          targetMax: 15,
          restSec: restLight,
          alternative: { de: "Trizeps-Maschine", en: "Triceps machine" },
        },
        {
          slug: "forearm-plank",
          sets: 3,
          targetMin: 20,
          targetMax: 40,
          restSec: restLight,
          alternative: {
            de: "Bauchmaschine 2 × 12–15",
            en: "Ab crunch machine 2 × 12–15",
          },
        },
      ],
    },
    {
      name: { de: "Workout B", en: "Workout B" },
      focus: {
        de: "Beine (geführte Kniebeuge), oberer und unterer Rücken, Schultern",
        en: "Legs (guided squat), upper and lower back, shoulders",
      },
      kind: "strength",
      exercises: [
        {
          slug: "smith-machine-squat",
          sets: 3,
          targetMin: 10,
          targetMax: 12,
          restSec: restHeavy,
          warmupSets: 2,
          alternative: {
            de: "Hackenschmidt-Maschine oder Beinpresse",
            en: "Hack squat machine or leg press",
          },
        },
        {
          slug: "machine-seated-cable-row",
          sets: 3,
          targetMin: 10,
          targetMax: 12,
          restSec: restHeavy,
          alternative: {
            de: "Rudern am Kabel oder einarmiges Kurzhantelrudern",
            en: "Cable row or one-arm dumbbell row",
          },
        },
        {
          slug: "machine-plate-loaded-incline-chest-press",
          sets: 3,
          targetMin: 10,
          targetMax: 12,
          restSec: restHeavy,
          alternative: {
            de: "Kurzhantel-Schrägbankdrücken",
            en: "Incline dumbbell press",
          },
        },
        {
          slug: "machine-45-degree-back-extension",
          sets: 2,
          targetMin: 12,
          targetMax: 15,
          restSec: restLight,
          alternative: {
            de: "Rückenstrecker-Maschine",
            en: "Back extension machine",
          },
        },
        {
          slug: "machine-standing-lateral-raise",
          sets: 2,
          targetMin: 12,
          targetMax: 15,
          restSec: restLight,
          alternative: {
            de: "Seitheben mit Kurzhanteln oder am Kabel",
            en: "Dumbbell or cable lateral raise",
          },
        },
        {
          slug: "cable-bar-curl",
          sets: 2,
          targetMin: 12,
          targetMax: 15,
          restSec: restLight,
          alternative: {
            de: "Bizeps-Maschine oder Kurzhantel-Curls",
            en: "Biceps machine or dumbbell curls",
          },
        },
        {
          slug: "machine-crunch",
          sets: 2,
          targetMin: 12,
          targetMax: 15,
          restSec: restLight,
          alternative: {
            de: "Beinheben liegend auf der Bank",
            en: "Lying leg raises on a bench",
          },
        },
      ],
    },
    {
      name: { de: "Laufband", en: "Treadmill" },
      focus: {
        de: "An 1–2 Tagen ohne Krafttraining, z. B. Di und Sa",
        en: "On 1–2 days without strength training, e.g. Tue and Sat",
      },
      kind: "cardio",
      exercises: [
        {
          slug: "treadmill-run",
          sets: 1,
          targetMin: 20,
          targetMax: 30,
          restSec: 0,
          alternative: {
            de: "Rad oder Crosstrainer",
            en: "Bike or cross-trainer",
          },
        },
      ],
    },
  ],
};

// --- more templates ---------------------------------------------------------------------

const restMain = 120;

/** Shorthand for a workout exercise of a template. */
const ex = (
  slug: string,
  sets: number,
  targetMin: number,
  targetMax: number,
  restSec: number,
  alternative: LocalizedText,
  warmupSets = 0,
): WorkoutExerciseSeed => ({ slug, sets, targetMin, targetMax, restSec, warmupSets, alternative });

const ALT = {
  dbBench: { de: "Kurzhantel-Bankdrücken", en: "Dumbbell bench press" },
  dbIncline: { de: "Kurzhantel-Schrägbankdrücken", en: "Incline dumbbell press" },
  dbShoulder: { de: "Kurzhantel-Schulterdrücken sitzend", en: "Seated dumbbell shoulder press" },
  assistedPullup: { de: "Klimmzug-Maschine mit Unterstützung", en: "Assisted pull-up machine" },
  dbRow: { de: "Einarmiges Kurzhantelrudern", en: "One-arm dumbbell row" },
  tricepsMachine: { de: "Trizeps-Maschine", en: "Triceps machine" },
  bicepsMachine: { de: "Bizeps-Maschine oder Kurzhantel-Curls", en: "Biceps machine or dumbbell curls" },
  lateral: { de: "Seitheben mit Kurzhanteln oder am Kabel", en: "Dumbbell or cable lateral raise" },
  reversePecDeck: { de: "Reverse Butterfly an der Maschine", en: "Reverse pec deck" },
  hackSquat: { de: "Hackenschmidt-Maschine oder Goblet Squat", en: "Hack squat machine or goblet squat" },
  lyingLegCurl: { de: "Beinbeuger liegend", en: "Lying leg curl" },
  legPressCalf: { de: "Wadenheben an der Beinpresse", en: "Calf raise on the leg press" },
  legRaise: { de: "Beinheben liegend auf der Bank", en: "Lying leg raises on a bench" },
  backExtension: { de: "Rückenstrecker-Maschine (45°)", en: "45° back extension" },
  splitSquat: { de: "Split Squat mit Kurzhanteln", en: "Dumbbell split squat" },
  abMachine: { de: "Bauchmaschine 2 × 12–15", en: "Ab crunch machine 2 × 12–15" },
} satisfies Record<string, LocalizedText>;

const GYM_WARMUP: LocalizedList = {
  de: [
    "5 Min. Crosstrainer, Rad oder zügiges Gehen am Laufband.",
    "Je 10× Armkreisen, Hüftkreisen und Kniebeugen ohne Gewicht.",
    "Bei der ersten Übung 1–2 Aufwärmsätze mit ca. 50 % des Arbeitsgewichts.",
  ],
  en: [
    "5 min cross-trainer, bike or brisk treadmill walk.",
    "10× each: arm circles, hip circles and bodyweight squats.",
    "For the first exercise, 1–2 warm-up sets at about 50% of your working weight.",
  ],
};

const TREADMILL_WORKOUT = (focus: LocalizedText): PlanTemplate["workouts"][number] => ({
  name: { de: "Laufband", en: "Treadmill" },
  focus,
  kind: "cardio",
  exercises: [ex("treadmill-run", 1, 20, 30, 0, { de: "Rad oder Crosstrainer", en: "Bike or cross-trainer" })],
});

const LEARN_PHASE = (goal: LocalizedText): Phase => ({
  id: "p1",
  fromWeek: 1,
  toWeek: 2,
  setsMode: "fixed",
  fixedSets: 2,
  rir: "3",
  goal,
});

export const UPPER_LOWER: PlanTemplate = {
  key: "upper-lower-4x",
  name: { de: "Ober-/Unterkörper (4×/Woche)", en: "Upper/lower split (4×/week)" },
  description: {
    de: "Der nächste Schritt nach dem Ganzkörper-Starter: 4× pro Woche abwechselnd Ober- und Unterkörper, je ca. 45–55 Minuten. Mehr Sätze pro Muskel und etwas schwerer (meist 8–12 Wiederholungen). Maschinen, Kabelzüge und einige Kurzhantelübungen.",
    en: "The next step after the full-body starter: upper and lower body alternating 4× per week, about 45–55 minutes each. More sets per muscle and a little heavier (mostly 8–12 reps). Machines, cables and a few dumbbell exercises.",
  },
  trainingDays: [0, 1, 3, 4],
  cardioDays: [5],
  warmup: GYM_WARMUP,
  tips: {
    de: [
      "Reihenfolge: Mo Oberkörper A, Di Unterkörper A, Do Oberkörper B, Fr Unterkörper B. Die App wählt automatisch das nächste Workout.",
      "Steigere das Gewicht, sobald du in allen Sätzen das obere Ende der Wiederholungen sauber schaffst.",
      "Die letzten 1–2 Wiederholungen dürfen schwer sein, die Technik muss aber sauber bleiben.",
      "Schlaf und genug Eiweiß (ca. 1,6 g pro kg Körpergewicht) bringen mehr als zusätzliche Sätze.",
      "Fühlst du dich nach einigen Wochen dauerhaft schlapp? Eine Woche mit halb so vielen Sätzen einlegen.",
    ],
    en: [
      "Order: Mon upper A, Tue lower A, Thu upper B, Fri lower B. The app picks the next workout automatically.",
      "Add weight once you hit the top of the rep range cleanly in every set.",
      "The last 1–2 reps may be hard, but your technique has to stay clean.",
      "Sleep and enough protein (about 1.6 g per kg of body weight) do more than extra sets.",
      "Feeling run down for weeks? Take a week with half the sets.",
    ],
  },
  phases: [
    LEARN_PHASE({
      de: "Neue Übungen kennenlernen und Startgewichte finden. Noch nicht bis ans Limit.",
      en: "Get to know the new exercises and find your starting weights. Not to your limit yet.",
    }),
    {
      id: "p2",
      fromWeek: 3,
      toWeek: 8,
      setsMode: "plan",
      rir: "1–2",
      goal: {
        de: "Gewicht steigern, sobald du in allen Sätzen das obere Ende der Wiederholungen schaffst.",
        en: "Add weight once you hit the top of the rep range in every set.",
      },
    },
    {
      id: "p3",
      fromWeek: 9,
      toWeek: null,
      setsMode: "bonus",
      bonusSets: 1,
      bonusFirstN: 2,
      rir: "1–2",
      goal: {
        de: "Die ersten zwei Übungen jedes Workouts bekommen einen Satz mehr.",
        en: "The first two exercises of every workout get one more set.",
      },
    },
  ],
  workouts: [
    {
      name: { de: "Oberkörper A", en: "Upper A" },
      focus: { de: "Brust, Rücken, Schultern, Arme (Drücken zuerst)", en: "Chest, back, shoulders, arms (pressing first)" },
      kind: "strength",
      exercises: [
        ex("machine-chest-press", 3, 8, 10, restMain, ALT.dbBench, 1),
        ex("machine-pulldown", 3, 8, 10, restMain, ALT.assistedPullup),
        ex("machine-neutral-overhead-press", 2, 10, 12, restHeavy, ALT.dbShoulder),
        ex("machine-seated-cable-row", 2, 10, 12, restHeavy, ALT.dbRow),
        ex("cable-rope-pushdown", 2, 12, 15, restLight, ALT.tricepsMachine),
        ex("cable-bar-curl", 2, 12, 15, restLight, ALT.bicepsMachine),
      ],
    },
    {
      name: { de: "Unterkörper A", en: "Lower A" },
      focus: { de: "Beine (Beinpresse), Beinbeuger, Waden, Bauch", en: "Legs (leg press), hamstrings, calves, abs" },
      kind: "strength",
      exercises: [
        ex("machine-leg-press", 3, 8, 10, restMain, ALT.hackSquat, 2),
        ex("machine-seated-leg-curl", 3, 10, 12, restHeavy, ALT.lyingLegCurl),
        ex("machine-leg-extension", 2, 12, 15, restLight, ALT.splitSquat),
        ex("machine-calf-raise", 3, 12, 15, restLight, ALT.legPressCalf),
        ex("machine-crunch", 2, 12, 15, restLight, ALT.legRaise),
      ],
    },
    {
      name: { de: "Oberkörper B", en: "Upper B" },
      focus: {
        de: "Rücken, obere Brust, seitliche Schultern, Arme (Ziehen zuerst)",
        en: "Back, upper chest, side delts, arms (pulling first)",
      },
      kind: "strength",
      exercises: [
        ex("machine-seated-cable-row", 3, 8, 10, restMain, ALT.dbRow, 1),
        ex("machine-plate-loaded-incline-chest-press", 3, 8, 10, restMain, ALT.dbIncline),
        ex("machine-pec-fly", 2, 12, 15, restLight, { de: "Fliegende am Kabelzug", en: "Cable fly" }),
        ex("machine-standing-lateral-raise", 3, 12, 15, restLight, ALT.lateral),
        ex("cable-face-pull", 2, 12, 15, restLight, ALT.reversePecDeck),
        ex("dumbbell-hammer-curl", 2, 10, 12, restLight, { de: "Bizeps-Maschine", en: "Biceps machine" }),
      ],
    },
    {
      name: { de: "Unterkörper B", en: "Lower B" },
      focus: {
        de: "Beine (geführte Kniebeuge), hintere Oberschenkel, Waden, Rumpf",
        en: "Legs (guided squat), hamstrings, calves, core",
      },
      kind: "strength",
      exercises: [
        ex("smith-machine-squat", 3, 8, 10, restMain, { de: "Hackenschmidt-Maschine oder Beinpresse", en: "Hack squat machine or leg press" }, 2),
        ex("dumbbell-romanian-deadlift", 3, 10, 12, restHeavy, ALT.backExtension),
        ex("dumbbell-split-squat", 2, 10, 12, restLight, { de: "Beinpresse einbeinig", en: "Single-leg leg press" }),
        ex("machine-calf-raise", 3, 12, 15, restLight, ALT.legPressCalf),
        ex("forearm-plank", 3, 30, 60, restLight, ALT.abMachine),
      ],
    },
    TREADMILL_WORKOUT({ de: "An einem freien Tag, z. B. Sa", en: "On a free day, e.g. Sat" }),
  ],
};

export const PUSH_PULL_LEGS: PlanTemplate = {
  key: "push-pull-legs-3x",
  name: { de: "Push/Pull/Beine (3×/Woche)", en: "Push/pull/legs (3×/week)" },
  description: {
    de: "Drücken, Ziehen und Beine an drei Tagen, je ca. 50 Minuten. Jede Muskelgruppe bekommt eine eigene Einheit mit mehr Sätzen. Wer mehr Zeit hat, trainiert die Runde zweimal pro Woche (6 Tage).",
    en: "Push, pull and legs on three days, about 50 minutes each. Every muscle group gets its own session with more sets. With more time, run the rotation twice a week (6 days).",
  },
  trainingDays: [0, 2, 4],
  cardioDays: [1, 5],
  warmup: GYM_WARMUP,
  tips: {
    de: [
      "Push = Brust, Schultern, Trizeps. Pull = Rücken, Bizeps. Beine = Oberschenkel, Po, Waden.",
      "Für 6 Tage pro Woche: im Plan Mo–Sa als Trainingstage wählen. Die App wechselt Push, Pull und Beine automatisch durch.",
      "Steigere das Gewicht, sobald du in allen Sätzen das obere Ende der Wiederholungen sauber schaffst.",
      "Bei 6 Tagen besonders auf Schlaf achten und nach 6–8 Wochen eine leichtere Woche einlegen.",
    ],
    en: [
      "Push = chest, shoulders, triceps. Pull = back, biceps. Legs = thighs, glutes, calves.",
      "For 6 days a week: pick Mon–Sat as training days in the plan. The app cycles through push, pull and legs automatically.",
      "Add weight once you hit the top of the rep range cleanly in every set.",
      "On 6 days, take extra care of your sleep and add a lighter week after 6–8 weeks.",
    ],
  },
  phases: [
    LEARN_PHASE({
      de: "Neue Übungen kennenlernen und Startgewichte finden. Noch nicht bis ans Limit.",
      en: "Get to know the new exercises and find your starting weights. Not to your limit yet.",
    }),
    {
      id: "p2",
      fromWeek: 3,
      toWeek: null,
      setsMode: "plan",
      rir: "1–2",
      goal: {
        de: "Gewicht steigern, sobald du in allen Sätzen das obere Ende der Wiederholungen schaffst.",
        en: "Add weight once you hit the top of the rep range in every set.",
      },
    },
  ],
  workouts: [
    {
      name: { de: "Push", en: "Push" },
      focus: { de: "Brust, Schultern, Trizeps", en: "Chest, shoulders, triceps" },
      kind: "strength",
      exercises: [
        ex("machine-chest-press", 3, 8, 10, restMain, ALT.dbBench, 2),
        ex("machine-plate-loaded-incline-chest-press", 3, 10, 12, restHeavy, ALT.dbIncline),
        ex("machine-neutral-overhead-press", 3, 10, 12, restHeavy, ALT.dbShoulder),
        ex("machine-standing-lateral-raise", 3, 12, 15, restLight, ALT.lateral),
        ex("cable-rope-pushdown", 2, 12, 15, restLight, ALT.tricepsMachine),
        ex("cable-overhead-triceps-extension", 2, 12, 15, restLight, {
          de: "Trizepsstrecken über Kopf mit einer Kurzhantel",
          en: "Overhead dumbbell triceps extension",
        }),
      ],
    },
    {
      name: { de: "Pull", en: "Pull" },
      focus: { de: "Rücken, hintere Schultern, Bizeps", en: "Back, rear delts, biceps" },
      kind: "strength",
      exercises: [
        ex("machine-pulldown", 3, 8, 10, restMain, ALT.assistedPullup, 1),
        ex("machine-seated-cable-row", 3, 10, 12, restHeavy, ALT.dbRow),
        ex("cable-face-pull", 3, 12, 15, restLight, ALT.reversePecDeck),
        ex("machine-45-degree-back-extension", 2, 12, 15, restLight, { de: "Rückenstrecker-Maschine", en: "Back extension machine" }),
        ex("cable-bar-curl", 2, 10, 12, restLight, { de: "Bizeps-Maschine", en: "Biceps machine" }),
        ex("dumbbell-hammer-curl", 2, 10, 12, restLight, { de: "Hammercurls am Kabel mit Seil", en: "Cable rope hammer curl" }),
      ],
    },
    {
      name: { de: "Beine", en: "Legs" },
      focus: { de: "Oberschenkel, Po, Waden, Bauch", en: "Thighs, glutes, calves, abs" },
      kind: "strength",
      exercises: [
        ex("machine-leg-press", 3, 8, 10, restMain, { de: "Hackenschmidt-Maschine oder Multipresse", en: "Hack squat or Smith machine squat" }, 2),
        ex("dumbbell-romanian-deadlift", 3, 10, 12, restHeavy, ALT.backExtension),
        ex("machine-leg-extension", 3, 12, 15, restLight, ALT.splitSquat),
        ex("machine-seated-leg-curl", 3, 10, 12, restLight, ALT.lyingLegCurl),
        ex("machine-calf-raise", 3, 12, 15, restLight, ALT.legPressCalf),
        ex("machine-crunch", 2, 12, 15, restLight, ALT.legRaise),
      ],
    },
    TREADMILL_WORKOUT({
      de: "An 1–2 Tagen ohne Krafttraining, z. B. Di und Sa",
      en: "On 1–2 days without strength training, e.g. Tue and Sat",
    }),
  ],
};

export const HOME_DUMBBELL: PlanTemplate = {
  key: "home-dumbbell-3x",
  name: { de: "Zuhause mit Kurzhanteln (3×/Woche)", en: "Home dumbbell workout (3×/week)" },
  description: {
    de: "3× pro Woche Ganzkörper zu Hause, abwechselnd Workout A und B, je ca. 45 Minuten. Du brauchst nur ein Paar verstellbare Kurzhanteln (ca. 2–20 kg), eine Matte und einen stabilen Stuhl oder ein Sofa. Keine Hantelbank nötig.",
    en: "Full body 3× per week at home, alternating workout A and B, about 45 minutes each. All you need is a pair of adjustable dumbbells (about 2–20 kg), a mat and a sturdy chair or sofa. No bench required.",
  },
  trainingDays: [0, 2, 4],
  cardioDays: [1, 5],
  warmup: {
    de: [
      "3–5 Min. auf der Stelle gehen, Hampelmänner oder Seilspringen.",
      "Je 10× Armkreisen, Hüftkreisen und Kniebeugen ohne Gewicht.",
      "Bei der ersten Übung 1 Aufwärmsatz mit leichtem Gewicht.",
    ],
    en: [
      "3–5 min marching on the spot, jumping jacks or skipping rope.",
      "10× each: arm circles, hip circles and bodyweight squats.",
      "For the first exercise, 1 warm-up set with a light weight.",
    ],
  },
  tips: {
    de: [
      "Verstellbare Kurzhanteln sparen Platz und Geld. Achte darauf, dass die Verschlüsse fest sitzen.",
      "Eine Übung ist selbst mit dem schwersten Gewicht zu leicht? Langsamer absenken (3–4 Sekunden) oder die Pausen kürzen.",
      "Liegestütz zu schwer? Hände erhöht auf einen Tisch oder die Sofalehne stützen.",
      "Bei einseitigen Übungen (Rudern, Split Squat) gelten die Wiederholungen pro Seite.",
      "Steigere das Gewicht, sobald du in allen Sätzen das obere Ende der Wiederholungen sauber schaffst.",
    ],
    en: [
      "Adjustable dumbbells save space and money. Make sure the collars are tight.",
      "An exercise is too easy even with the heaviest weight? Lower more slowly (3–4 seconds) or shorten the rest.",
      "Push-ups too hard? Put your hands up on a table or the arm of the sofa.",
      "For one-sided exercises (row, split squat) the reps are per side.",
      "Add weight once you hit the top of the rep range cleanly in every set.",
    ],
  },
  phases: [
    LEARN_PHASE({
      de: "Technik lernen und passende Gewichte finden. Leicht trainieren.",
      en: "Learn the technique and find the right weights. Keep it light.",
    }),
    {
      id: "p2",
      fromWeek: 3,
      toWeek: null,
      setsMode: "plan",
      rir: "1–2",
      goal: {
        de: "Steigern, sobald du in allen Sätzen das obere Ende der Wiederholungen schaffst.",
        en: "Progress once you reach the top of the rep range in every set.",
      },
    },
  ],
  workouts: [
    {
      name: { de: "Workout A", en: "Workout A" },
      focus: { de: "Beine (Kniebeuge), Brust, Rücken, Bauch", en: "Legs (squat), chest, back, core" },
      kind: "strength",
      exercises: [
        ex("dumbbell-goblet-squat", 3, 10, 15, restHeavy, { de: "Langsame Kniebeugen ohne Gewicht", en: "Slow bodyweight squats" }, 1),
        ex("dumbbell-floor-press", 3, 8, 12, restHeavy, { de: "Liegestütz", en: "Push-ups" }),
        ex("dumbbell-one-arm-row", 3, 10, 12, restLight, {
          de: "Vorgebeugtes Rudern mit beiden Kurzhanteln",
          en: "Bent-over row with both dumbbells",
        }),
        ex("glute-bridge", 2, 12, 15, restLight, { de: "Hüftheben einbeinig", en: "Single-leg glute bridge" }),
        ex("dumbbell-hammer-curl", 2, 10, 12, restLight, { de: "Bizeps-Curls mit Kurzhanteln", en: "Dumbbell biceps curls" }),
        ex("forearm-plank", 3, 20, 40, restLight, { de: "Unterarmstütz auf den Knien", en: "Plank on your knees" }),
      ],
    },
    {
      name: { de: "Workout B", en: "Workout B" },
      focus: { de: "Hintere Oberschenkel, Brust, Beine (Split Squat), Schultern", en: "Hamstrings, chest, legs (split squat), shoulders" },
      kind: "strength",
      exercises: [
        ex("dumbbell-romanian-deadlift", 3, 10, 12, restHeavy, { de: "Hüftheben mit Kurzhantel", en: "Glute bridge with a dumbbell" }, 1),
        ex("push-up", 3, 6, 15, restHeavy, {
          de: "Liegestütz mit den Händen auf einem Tisch",
          en: "Incline push-ups with your hands on a table",
        }),
        ex("dumbbell-split-squat", 2, 8, 12, restLight, { de: "Ausfallschritte rückwärts", en: "Reverse lunges" }),
        ex("dumbbell-shoulder-press", 3, 8, 12, restLight, { de: "Einarmiges Schulterdrücken", en: "One-arm shoulder press" }),
        ex("dumbbell-one-arm-row", 2, 10, 12, restLight, {
          de: "Vorgebeugtes Rudern mit beiden Kurzhanteln",
          en: "Bent-over row with both dumbbells",
        }),
        ex("forearm-plank", 3, 20, 40, restLight, { de: "Unterarmstütz auf den Knien", en: "Plank on your knees" }),
      ],
    },
    {
      name: { de: "Gehen oder Laufen", en: "Walk or run" },
      focus: {
        de: "An 1–2 Tagen ohne Krafttraining, z. B. Di und Sa",
        en: "On 1–2 days without strength training, e.g. Tue and Sat",
      },
      kind: "cardio",
      exercises: [ex("walk-or-run", 1, 20, 40, 0, { de: "Rad fahren oder Seilspringen", en: "Cycling or skipping rope" })],
    },
  ],
};

export const PLAN_TEMPLATES: PlanTemplate[] = [FULLBODY_STARTER, UPPER_LOWER, PUSH_PULL_LEGS, HOME_DUMBBELL];
